import * as React from "react";
import { FormComponent } from "../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../react-core/Html/SelectEditor";
import { CommonHelper } from "../../../../react-core/Helpers/CommonHelper";
import { TextBoxEditor } from "../../../../react-core/Html/TextBoxEditor";
import { DatePicker } from "../../../../react-core/Datepicker/DatePicker";
import { ContentDatePicker, ACCEPT_DATE_FORMATS } from "../../../Shared/Entities/Constants/DateConstants";
import { ContentFilter, ProcessTypeOptions, NetworkTypeOptions, CreatedbyOptions, errorMsg } from "../Resource/Content";
import { JsonDateParser } from "../../../../react-core/Utils/JsonDateParser";
import { NoSpecial, Numeric } from "../../../../react-core/Html/Validation/RegExPatterns";

interface INetworkFilterProps {
    filter: any;
    handleFilterChange: (changes) => void;
    onSearch: VoidFunction;
}

interface INetworkFilterState {}

export class NetworkFilter extends FormComponent<INetworkFilterProps, INetworkFilterState> {
    constructor(props: INetworkFilterProps) {
        super(props);
        this.state = {};
    }

    public render(): JSX.Element {
        return (
            <>
                {this.renderFilters()}
                {this.renderDateRanges()}
            </>
        );
    }

    private renderFilters(): JSX.Element {
        const { NetworkProcessType, NetworkType, NetworkName, TradingReference, CreateUserId } = this.props.filter;
        const { handleFilterChange } = this.props;
        return (
            <>
                <h1 className="page-heading">{ContentFilter.title.title}</h1>
                <div className="row narrow-gutter">
                    <div className="col-12 col-sm-4">
                        <SelectEditor
                            label={ContentFilter.search.processType}
                            selected={NetworkProcessType}
                            options={ProcessTypeOptions.map((item) => item.key)}
                            getText={(key) => CommonHelper.getOptionsTxt(key, ProcessTypeOptions)}
                            onChange={(v) => handleFilterChange({ NetworkProcessType: v })}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <SelectEditor
                            label={ContentFilter.search.networkType}
                            selected={NetworkType}
                            options={NetworkTypeOptions.map((item) => item.key)}
                            getText={(key) => CommonHelper.getOptionsTxt(key, NetworkTypeOptions)}
                            onChange={(v) => handleFilterChange({ NetworkType: v })}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <TextBoxEditor
                            value={NetworkName}
                            label={ContentFilter.search.networkName}
                            maxLength={50}
                            regex={{ errorMessage: errorMsg.noSpecial, value: NoSpecial }}
                            onChange={(v) => handleFilterChange({ NetworkName: v })}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <TextBoxEditor
                            value={TradingReference}
                            label={ContentFilter.search.reference}
                            onChange={(v) => handleFilterChange({ TradingReference: v })}
                            maxLength={8}
                            regex={{ errorMessage: "Must be numbers", value: Numeric }}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <SelectEditor
                            label={ContentFilter.search.createdBy}
                            selected={CreateUserId}
                            options={CreatedbyOptions.map((item) => item.key)}
                            getText={(key) => CommonHelper.getOptionsTxt(key, CreatedbyOptions)}
                            onChange={(v) => handleFilterChange({ CreateUserId: v == "1" ? 1 : null })}
                        />
                    </div>
                </div>
            </>
        );
    }

    private renderDateRanges(): JSX.Element {
        const { StartedFromDate, StartedToDate, UpdatedFromDate, UpdatedToDate } = this.props.filter;
        const { handleFilterChange, onSearch } = this.props;
        return (
            <>
                <div className="row narrow-gutter">
                    <div className="col-xs-6 col-sm-fluid">
                        <h3>{ContentFilter.search.dateStarted}</h3>
                        <div className="row narrow-gutter">
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.search.from}
                                    selected={JsonDateParser.toDateNoOffset(StartedFromDate)}
                                    atLeast={
                                        StartedFromDate
                                            ? {
                                                  value: ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        StartedFromDate
                                            ? {
                                                  value: JsonDateParser.toDateNoOffset(StartedToDate)
                                                      ? JsonDateParser.toDateNoOffset(StartedToDate)
                                                      : new Date(),
                                                  errorMessage: ContentDatePicker.CannotBeInTheFuture,
                                              }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) =>
                                        handleFilterChange({ StartedFromDate: JsonDateParser.toJsonDateNoOffset(d) })
                                    }
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.search.to}
                                    selected={JsonDateParser.toDateNoOffset(StartedToDate)}
                                    atLeast={
                                        StartedToDate
                                            ? {
                                                  value: JsonDateParser.toDateNoOffset(StartedFromDate)
                                                      ? JsonDateParser.toDateNoOffset(StartedFromDate)
                                                      : ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        StartedToDate
                                            ? { value: new Date(), errorMessage: ContentDatePicker.CannotBeInTheFuture }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) =>
                                        handleFilterChange({ StartedToDate: JsonDateParser.toJsonDateNoOffset(d) })
                                    }
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6 col-sm-fluid">
                        <h3>{ContentFilter.search.lastUpdated}</h3>
                        <div className="row narrow-gutter">
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.search.from}
                                    selected={JsonDateParser.toDateNoOffset(UpdatedFromDate)}
                                    atLeast={
                                        UpdatedFromDate
                                            ? {
                                                  value: ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        UpdatedFromDate
                                            ? {
                                                  value: JsonDateParser.toDateNoOffset(UpdatedToDate)
                                                      ? JsonDateParser.toDateNoOffset(UpdatedToDate)
                                                      : new Date(),
                                                  errorMessage: ContentDatePicker.CannotBeInTheFuture,
                                              }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) =>
                                        handleFilterChange({ UpdatedFromDate: JsonDateParser.toJsonDateNoOffset(d) })
                                    }
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.search.to}
                                    selected={JsonDateParser.toDateNoOffset(UpdatedToDate)}
                                    atLeast={
                                        UpdatedToDate
                                            ? {
                                                  value: JsonDateParser.toDateNoOffset(UpdatedFromDate)
                                                      ? JsonDateParser.toDateNoOffset(UpdatedFromDate)
                                                      : ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        UpdatedToDate
                                            ? { value: new Date(), errorMessage: ContentDatePicker.CannotBeInTheFuture }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) =>
                                        handleFilterChange({ UpdatedToDate: JsonDateParser.toJsonDateNoOffset(d) })
                                    }
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto col-bottom">
                        <div className="form-group">
                            <div className="controls">
                                <button
                                    className="btn secondary"
                                    onClick={() => {
                                        if (this.isValid()) {
                                            this.props.onSearch();
                                        }
                                    }}
                                >
                                    {ContentFilter.title.search}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
